import { useMemo, useState, type FormEvent } from "react";
import { Notice } from "@/components/crm/blocks";
import { OptionList } from "@/components/crm/OptionList";
import { RecordHistory } from "@/components/crm/RecordHistory";
import { Button } from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useCrm } from "@/hooks/useCrm";
import { ApiError, errorTitle } from "@/lib/api";
import { PERMISSIONS, SCHEMA, type FieldDef } from "@/lib/schema";
import type { CrmRecord, FieldValue, TableKey } from "@/lib/types";
import { cn } from "@/lib/utils";

// Bewerken (contract §6.4): handmatige correcties van elk veld van een record, opgebouwd uit
// shared/schema.json. Alleen gewijzigde velden gaan naar de server; een leeg veld wist de waarde.
// Velden met een extra recht (permissions.json → fieldGuards) zijn zonder dat recht niet te wijzigen.
// Onderaan: de historiek van het record (met het recht audit.lezen).

type Draft = Record<string, string | string[]>;

const pad = (n: number) => String(n).padStart(2, "0");

/** ISO-tijdstip ↔ waarde voor <input type="datetime-local"> in de tijd van de browser. */
function toLocalInput(value: FieldValue | undefined) {
  if (typeof value !== "string" || !value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function draftOf(field: FieldDef, value: FieldValue | undefined): string | string[] {
  if (field.type === "link") return Array.isArray(value) ? value : [];
  if (field.type === "datetime") return toLocalInput(value);
  return value === undefined ? "" : String(value);
}

/** Wat naar de server gaat: null = wissen, een getal voor getallen, ISO voor tijdstippen. */
function serverValue(field: FieldDef, draft: string | string[]): FieldValue | null {
  if (field.type === "link") return (draft as string[]).length ? (draft as string[]) : null;
  const raw = String(draft).trim();
  if (!raw) return null;
  if (field.type === "number" || field.type === "currency") return Number(raw.replace(",", "."));
  if (field.type === "datetime") return new Date(raw).toISOString();
  return raw;
}

function changed(field: FieldDef, before: FieldValue | undefined, after: FieldValue | null) {
  if (field.type === "link") return JSON.stringify([...((before as string[] | undefined) ?? [])].sort()) !== JSON.stringify([...((after as string[] | null) ?? [])].sort());
  if (field.type === "datetime" && before && after) return new Date(String(before)).getTime() !== new Date(String(after)).getTime();
  return (before ?? null) !== after;
}

export function EditDialog({ table, id, onClose }: { table: TableKey; id: string | null; onClose: () => void }) {
  const crm = useCrm();
  const def = SCHEMA.tables[table];
  const record: CrmRecord | null = id ? crm.get(table, id) : null;
  const [draft, setDraft] = useState<Draft>(() => Object.fromEntries(def.fields.map((f) => [f.name, draftOf(f, record?.fields[f.name])])));
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<{ title: string; message: string; details: { field: string; message: string }[] } | null>(null);
  const invalid = useMemo(() => new Set(failure?.details.map((d) => d.field)), [failure]);
  const guards = PERMISSIONS.fieldGuards[table] ?? {};

  if (id && !record) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Bewerken</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Notice tone="red">Dit record bestaat niet meer.</Notice>
          </DialogBody>
        </DialogContent>
      </Dialog>
    );
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const fields: Record<string, FieldValue | null> = {};
    for (const field of def.fields) {
      const value = serverValue(field, draft[field.name]!);
      if (changed(field, record?.fields[field.name], value)) fields[field.name] = value;
    }
    if (!record) for (const name of Object.keys(fields)) if (fields[name] === null) delete fields[name];
    if (!record && !fields[def.primary]) {
      setFailure({ title: "Verplicht veld", message: `Vul “${def.primary}” in.`, details: [{ field: def.primary, message: "Verplicht veld" }] });
      return;
    }
    if (!Object.keys(fields).length) {
      onClose();
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      const saved = await crm.saveRecord(table, id, fields);
      toast.success("Opgeslagen", { description: `${def.name}: ${saved.fields[def.primary] ?? "(naamloos)"}` });
      onClose();
    } catch (error) {
      setFailure({ title: errorTitle(error), message: error instanceof Error ? error.message : String(error), details: error instanceof ApiError ? error.details : [] });
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="max-w-[760px]" aria-describedby="edit-kicker">
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <DialogHeader>
            <DialogDescription id="edit-kicker" className="mt-0 text-[11px] font-extrabold uppercase tracking-[.11em] text-blush-ink">
              {def.name} · {record ? "bewerken" : "nieuw"}
            </DialogDescription>
            <DialogTitle>{record ? String(record.fields[def.primary] ?? "(naamloos)") : `Nieuw record in ${def.name}`}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {failure && (
              <Notice tone="red" title={failure.title} className="mb-3.5">
                {failure.message}
                {failure.details.length > 0 && (
                  <ul className="ml-4 mt-1 list-disc">
                    {failure.details.map((d) => (
                      <li key={d.field}>
                        {d.field}: {d.message}
                      </li>
                    ))}
                  </ul>
                )}
              </Notice>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              {def.fields.map((field) => {
                const guard = guards[field.name];
                const locked = Boolean(guard && !crm.can(guard));
                // "Status=Beschikbaar": die ene waarde vraagt een extra recht.
                const blockedChoices = Object.entries(guards)
                  .filter(([key, permission]) => key.startsWith(`${field.name}=`) && !crm.can(permission))
                  .map(([key]) => key.slice(field.name.length + 1));
                return (
                  <EditInput
                    key={field.name}
                    field={field}
                    value={draft[field.name]!}
                    current={record?.fields[field.name]}
                    locked={locked}
                    blockedChoices={blockedChoices}
                    invalid={invalid.has(field.name)}
                    onChange={(value) => setDraft((d) => ({ ...d, [field.name]: value }))}
                  />
                );
              })}
            </div>
            {record && <RecordHistory table={table} id={record.id} />}
          </DialogBody>
          <DialogFooter>
            <Button onClick={onClose} disabled={busy}>
              Annuleren
            </Button>
            <Button type="submit" variant="default" disabled={busy}>
              {busy ? "Bezig…" : failure ? "Opnieuw proberen" : "Opslaan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditInput({
  field,
  value,
  current,
  locked,
  blockedChoices,
  invalid,
  onChange
}: {
  field: FieldDef;
  value: string | string[];
  current: FieldValue | undefined;
  locked: boolean;
  blockedChoices: string[];
  invalid: boolean;
  onChange: (value: string | string[]) => void;
}) {
  const crm = useCrm();
  const id = `edit-${field.name.replace(/[^a-z0-9]/gi, "")}`;
  const wide = field.type === "longtext" || field.type === "link";
  const common = { id, disabled: locked, "aria-invalid": invalid || undefined, className: cn(invalid && "border-tone-red") };
  let control;
  if (field.type === "select") {
    const choices = (field.choices ?? []).filter((c) => !blockedChoices.includes(c) || c === current);
    control = (
      <NativeSelect {...common} value={String(value)} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {choices.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </NativeSelect>
    );
  } else if (field.type === "link") {
    const options = crm
      .all(field.table!)
      .map((r) => ({ value: r.id, label: crm.name(field.table!, r.id) }))
      .sort((a, b) => a.label.localeCompare(b.label, "nl"));
    control = locked ? <Input disabled value={crm.names(field.table!, value)} /> : <OptionList options={options} value={value as string[]} onChange={onChange} invalid={invalid} labelledBy={`${id}-label`} />;
  } else if (field.type === "longtext") {
    control = <Textarea {...common} value={String(value)} onChange={(e) => onChange(e.target.value)} />;
  } else {
    const types: Record<string, string> = { email: "email", phone: "tel", url: "url", number: "number", currency: "number", date: "date", datetime: "datetime-local" };
    control = <Input {...common} type={types[field.type] ?? "text"} step={field.type === "number" || field.type === "currency" ? "any" : undefined} value={String(value)} onChange={(e) => onChange(e.target.value)} />;
  }
  return (
    <div className={cn("grid content-start gap-1.5", wide && "sm:col-span-2")}>
      <label id={`${id}-label`} htmlFor={id} className="text-[11px] font-bold uppercase tracking-[.06em] text-navy-700">
        {field.name}
        {locked && <span className="ml-1 font-normal normal-case tracking-normal text-muted-foreground">(extra recht nodig)</span>}
      </label>
      {control}
    </div>
  );
}
